'use-client';

import { useEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import {
  Conditional,
  DealerLocatorSearchResult,
  DealerLocatorSearchResultLoader,
  DealerLocatorSearchResults as DealerLocatorSearchResultsBlocks,
  DealerLocatorViewMoreResultsButton,
  DealerStore,
  useBreakpoint,
  useDrawer
} from 'components/pcc-components';
import { UnitOfMeasure } from 'services/locations/types';
import { PAGE_BREAKPOINTS } from 'constants/common';
import {
  DEFAULT_NUM_RESULTS,
  SELECTED_STORE_ID,
  SELECT_STORE
} from '../constants';
import { ERROR, IDLE } from 'services/dealer-locator/constants';
import { STATUS_FAIL } from 'constants/analyticsConstants';
import { getRelativeURLWithParams, normalizeUrl } from 'utils/url';
import { replaceTokensInString } from '@cat-ecom/common/utils/stringUtils';
import { CONTACT_US_DEALER } from 'constants/endpoints';
import { useEsiteCtx } from 'services/esite/context';
import { getStoreHours } from 'services/location/utils';
import { useFormContext } from 'react-hook-form';
import { useFormStatus } from 'react-dom';
import { CatInlineNotification } from 'blocks-nextjs/blocks-react/components/InlineNotification';
import { CatIconWarningCircle } from 'blocks-nextjs/blocks-react/components/Icons/WarningCircle';
import { CatButton } from 'blocks-nextjs/blocks-react/components/Button';
import { CONTACT_DEALER_LINK } from 'constants/links';
import { normalizeCommerceLink } from 'utils/links';
import { useDealerLocatorLoader } from 'components/dealer-locator/dealer-locator-loader-context';
import { useDealerLocatorDrawer } from 'components/dealer-locator/dealer-locator-drawer-context';
import { SelectStoreResponse } from 'services/dealer-locator/types';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import { StoreDetailsServer } from '../dealer-locator-details-server';
import { PCC_DEALER_LOCATION_FLAG } from 'constants/featureFlag';
import { useLocationCtx } from 'services/location/context';
import { useDealerLocatorModal } from 'components/dealer-locator/dealer-locator-modal-context';
import { compareStoreLocations } from '../utils';

interface DealerLocatorSearchResultsProps {
  allFilteredDealerStoresByDistance: DealerStore[];
  filteredAndOrderedVisibleDealerStores: DealerStore[];
  isLoading: boolean;
  numDisplayed: number;
  setNumDisplayed: (num: number) => void;
  unitOfMeasure: UnitOfMeasure;
  isMapToggleChecked: boolean;
  selectStoreState?: SelectStoreResponse;
  showFilterErrorMessage: boolean;
  showNoStoresMapErrorMessage?: boolean;
  showNetworkErrorMessage: boolean;
  isSelectStoreError: boolean;
  setIsSelectStoreError: (value: boolean) => void;
  selectStoreAction: (payload: null) => void;
  isStoreLocationTandCFlag: boolean;
  storeDetailsDrawer?: ReturnType<typeof useDrawer>;
  setselectedStoreObjDrawer?: (arg0: DealerStore) => void;
}

export const DealerLocatorSearchResults = ({
  allFilteredDealerStoresByDistance,
  filteredAndOrderedVisibleDealerStores,
  isLoading,
  numDisplayed,
  setNumDisplayed,
  unitOfMeasure,
  isMapToggleChecked,
  selectStoreState,
  showFilterErrorMessage,
  showNoStoresMapErrorMessage,
  showNetworkErrorMessage,
  isSelectStoreError,
  setIsSelectStoreError,
  selectStoreAction,
  isStoreLocationTandCFlag,
  storeDetailsDrawer,
  setselectedStoreObjDrawer
}: DealerLocatorSearchResultsProps) => {
  const t = useTranslations();
  const { catalogId, langId, storeId } = useEsiteCtx();
  const { watch, setValue } = useFormContext();
  const { pending } = useFormStatus();
  const { isDealerLocatorLoading, setIsDealerLocatorLoading } =
    useDealerLocatorLoader();
  const { fireEvent } = useFormSubmitEvent();
  const { featureFlags } = useLocationCtx();
  const isDealerLocationFlag = featureFlags?.[PCC_DEALER_LOCATION_FLAG];
  const dealerLocatorDrawer = useDealerLocatorDrawer();
  const dealerLocatorModal = useDealerLocatorModal();
  const modalControl = dealerLocatorModal?.modalControl;

  const isStoreLocationListViewUpdateFlag =
    dealerLocatorDrawer?.isStoreLocationListViewUpdateFlag ?? false;
  const isStoreLocationMapViewUpdateFlag =
    dealerLocatorDrawer?.isStoreLocationMapViewUpdateFlag ?? false;
  const isStoreLocationOptimizationFlag =
    dealerLocatorDrawer?.isStoreLocationOptimizationFlag ?? false;
  const selectedStoreId = watch(SELECTED_STORE_ID);
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);

  const listedStores = useMemo(() => {
    if (isMapToggleChecked) {
      let selected;

      if (isStoreLocationListViewUpdateFlag && !isMD) {
        const sortedStores = [...allFilteredDealerStoresByDistance].sort(
          compareStoreLocations
        );
        const mappedStores = sortedStores.map((item, index) => ({
          ...item,
          geo: { ...item.geo, isClosest: index === 0 }
        }));

        selected = mappedStores.find(
          (store) => store.id.toString() === selectedStoreId
        );
      } else {
        selected = allFilteredDealerStoresByDistance.find(
          (store) => store.id.toString() === selectedStoreId
        );
      }

      return selected ? [selected] : [];
    }

    return filteredAndOrderedVisibleDealerStores;
  }, [
    allFilteredDealerStoresByDistance,
    filteredAndOrderedVisibleDealerStores,
    isMapToggleChecked,
    selectedStoreId,
    isStoreLocationListViewUpdateFlag,
    isMD
  ]);

  const selectedStore = filteredAndOrderedVisibleDealerStores?.find(
    ({ id }) => id.toString() === selectedStoreId
  );

  const showStoresList = !isLoading && !!listedStores[0];
  const moreResultsAvailable =
    allFilteredDealerStoresByDistance.length > numDisplayed;
  const showViewMoreButton = moreResultsAvailable && !isMapToggleChecked;
  const numSkeletonDisplayed = listedStores?.length || numDisplayed;

  const contactDealerLink = normalizeCommerceLink(
    getRelativeURLWithParams(CONTACT_DEALER_LINK, {
      catalogId,
      langId,
      storeId
    })
  );

  const handleViewMore = () => {
    const newNumDisplayed = numDisplayed + DEFAULT_NUM_RESULTS;
    setNumDisplayed(newNumDisplayed);
  };

  const returnContactUrl = (dealerStoreId?: string) =>
    normalizeUrl(
      replaceTokensInString(
        `${CONTACT_US_DEALER}`,
        catalogId,
        langId,
        dealerStoreId
      )
    );

  //set selected store id to default closest location when locations are changed
  useEffect(() => {
    if (!selectedStoreId && !!filteredAndOrderedVisibleDealerStores?.[0]?.id) {
      setValue(
        SELECTED_STORE_ID,
        filteredAndOrderedVisibleDealerStores[0].id.toString()
      );
    }
  }, [filteredAndOrderedVisibleDealerStores, selectedStoreId, setValue]);

  useEffect(() => {
    if (pending) {
      setIsDealerLocatorLoading(true);
      setIsSelectStoreError(false);
    } else if (selectStoreState?.message !== IDLE) {
      // Reset select store action state every time
      selectStoreAction(null);
      setIsDealerLocatorLoading(false);
      if (selectStoreState?.message === ERROR) {
        fireEvent({
          formName: SELECT_STORE,
          formContent: `${selectedStore?.dealerName}, ${selectedStore?.name}`,
          formStatus: STATUS_FAIL
        });
        setIsSelectStoreError(true);
      } else {
        dealerLocatorDrawer?.closeDrawer();
        modalControl?.close();
      }
    }
  }, [
    pending,
    setIsDealerLocatorLoading,
    fireEvent,
    selectStoreState,
    setIsSelectStoreError,
    selectStoreAction,
    selectedStore,
    dealerLocatorDrawer,
    modalControl
  ]);

  useEffect(() => {
    if (isLoading) {
      setIsSelectStoreError(false);
    }
  }, [isLoading, setIsSelectStoreError]);

  useEffect(() => {
    if (selectedStoreId) {
      setIsSelectStoreError(false);
    }
  }, [selectedStoreId, setIsSelectStoreError]);

  const getTitle = () => {
    const title = t('UNKNOWN_ERROR_TITLE');
    return `${title[0].toUpperCase()}${title.slice(1).toLowerCase()}`;
  };

  const errorCodeMessage = selectStoreState?.errorCode
    ? ` ${t('MLP_ERROR4', { 0: selectStoreState.errorCode })}`
    : '';

  return (
    <>
      <Conditional
        test={isSelectStoreError && !(isStoreLocationTandCFlag && !isMD)}
      >
        <div className="mt-4">
          <CatInlineNotification
            variant="warning"
            data-testid="timeout-error"
            heading={getTitle()}
          >
            <CatIconWarningCircle slot="before" data-testid="error-icon" />
            {`${t('UNKNOWN_ERROR_MESSAGE')}${errorCodeMessage}`}
            <div slot="after">
              <CatButton
                type="button"
                variant="ghost"
                className="text-nowrap mt-md-2 pt-2 pt-md-0"
                href={contactDealerLink}
              >
                {t('FOOTER_CONTACT_US')}
              </CatButton>
            </div>
          </CatInlineNotification>
        </div>
      </Conditional>
      <Conditional
        test={showNoStoresMapErrorMessage && !showFilterErrorMessage}
      >
        <div className="mt-4">
          <CatInlineNotification
            variant="error"
            data-testid="map-results-error"
          >
            <CatIconWarningCircle slot="before" data-testid="error-icon" />
            {t('FYD_BLOCKED_COUNTRY_LOCATION')}
            <div slot="after">
              <CatButton
                type="button"
                variant="ghost"
                className="text-nowrap pt-2 pt-md-0"
                href={contactDealerLink}
              >
                {t('FOOTER_CONTACT_US')}
              </CatButton>
            </div>
          </CatInlineNotification>
        </div>
      </Conditional>
      <Conditional test={showNetworkErrorMessage}>
        <div className="mt-4">
          <CatInlineNotification
            variant="error"
            data-testid="map-results-error"
            heading={getTitle()}
          >
            <CatIconWarningCircle slot="before" data-testid="error-icon" />
            {t('UNKNOWN_ERROR_MESSAGE')}
            <div slot="after">
              <CatButton
                type="button"
                variant="ghost"
                className="text-nowrap mt-md-2 pt-2 pt-md-0"
                href={contactDealerLink}
              >
                {t('FOOTER_CONTACT_US')}
              </CatButton>
            </div>
          </CatInlineNotification>
        </div>
      </Conditional>
      <Conditional test={showFilterErrorMessage}>
        <div className="mt-4">
          <CatInlineNotification
            variant={'error'}
            data-testid="filter-results-error"
          >
            <CatIconWarningCircle slot="before" data-testid="error-icon" />
            {`${t('FYD_NO_RESULTS')} ${t('FYD_ADJUST_SEARCH')}`}
          </CatInlineNotification>
        </div>
      </Conditional>
      <Conditional test={showStoresList}>
        <DealerLocatorSearchResultsBlocks visibleStores={listedStores}>
          {({ store }) => {
            const { id, storeHours } = store;
            store = {
              ...store,
              storeHours: getStoreHours(storeHours, t('OPEN_24_HOUR'))
            };
            return (
              <DealerLocatorSearchResult
                isDisabled={isDealerLocatorLoading}
                key={id}
                unitOfMeasure={unitOfMeasure}
                closestLabel={t('FYD_RESULTS_CLOSEST')}
                store={store}
                isMapView={isMapToggleChecked}
                isStoreLocationListViewUpdateFlag={
                  isStoreLocationListViewUpdateFlag
                }
                isStoreLocationMapViewUpdateFlag={
                  isStoreLocationMapViewUpdateFlag
                }
                isStoreLocationOptimizationFlag={
                  isStoreLocationOptimizationFlag
                }
                storeDetailsLabel={t('INV_STORE_DETAILS')}
                storeDetailsDrawer={storeDetailsDrawer}
                setselectedStoreObjDrawer={setselectedStoreObjDrawer}
              >
                <StoreDetailsServer
                  store={store}
                  returnContactUrl={returnContactUrl}
                  isDealerLocationFlag={isDealerLocationFlag}
                />
              </DealerLocatorSearchResult>
            );
          }}
        </DealerLocatorSearchResultsBlocks>
        <Conditional test={showViewMoreButton}>
          <DealerLocatorViewMoreResultsButton
            isDisabled={isDealerLocatorLoading}
            onClick={handleViewMore}
          >
            {t('VIEW_MORE_PRODUCTS')}
          </DealerLocatorViewMoreResultsButton>
        </Conditional>
      </Conditional>
      <Conditional test={isLoading}>
        <DealerLocatorSearchResultLoader
          numSkeletonLoaders={numSkeletonDisplayed}
        />
      </Conditional>
    </>
  );
};
